import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { IconComponent } from '../../../../../../shared/components/icon/icon.component';

@Component({
  selector: 'app-chart-year-selection',
  templateUrl: './chart-year-selection.component.html',
  imports: [IconComponent, NgIf],
  styleUrls: [
    './chart-year-selection.component.scss',
    '../../../../../../app.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartYearSelectionComponent implements DoCheck {
  @Output() selectedYearChange = new EventEmitter<{
    year: number;
    usedFor: string;
  }>();

  @Input() isChartVisible: boolean = false;
  @Input() accessibleYears!: number[];
  @Input() selectedYear!: number;
  @Input() usedFor!: string;

  private checkedYears = '';

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * `accessibleYears` is a live array owned by the group's service, refilled
   * in place on every fetch (see `setAccessibleYears`), so a new cell can
   * change which years exist without any input binding changing reference -
   * and a new cell often re-pins `selectedYear` to the same newest year, so
   * nothing marks this view dirty at all. The arrows would then keep the
   * previous cell's enabled state, and a disabled arrow has no click left to
   * heal itself with.
   */
  ngDoCheck(): void {
    const years = this.accessibleYears?.join() ?? '';

    if (years !== this.checkedYears) {
      this.checkedYears = years;
      this.cdr.markForCheck();
    }
  }

  // accessibleYears can skip years (health's list is only as complete as the
  // API response), so stepping moves by array index, never by year +/- 1.
  public get currentYearIndex(): number {
    return this.accessibleYears.indexOf(this.selectedYear);
  }

  // A selected year that is not in the list at all indexes to -1, and stepping
  // from there would jump to whichever end the direction points at rather than
  // to a neighbour - so there is nothing sensible to step to in either
  // direction.
  public get canStepBack(): boolean {
    return this.currentYearIndex > 0;
  }

  public get canStepForward(): boolean {
    const index = this.currentYearIndex;

    return index >= 0 && index < this.accessibleYears.length - 1;
  }

  public stepYear(direction: -1 | 1): void {
    if (direction === -1 ? !this.canStepBack : !this.canStepForward) {
      return;
    }

    this.selectedYearChange.emit({
      year: this.accessibleYears[this.currentYearIndex + direction],
      usedFor: this.usedFor,
    });
  }
}
